import React, { useRef, useCallback, useState, useEffect, createContext, useContext }from 'react';
import { createPortal } from 'react-dom';
import { AgGridReact } from 'ag-grid-react';
import { themeAlpine } from 'ag-grid-community';
import { apiProvider } from "@process-advisor/process-miner/api/apiProvider";
import '@process-advisor/process-miner/shared/gridStyles.css'
import '@process-advisor/process-miner/dashboard/shared/evidenceGridStyles.css';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { AppButton as Button } from '@advisor-core/components/AppButton';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { openFindingForContext, setFindingContextFromGridRow } from '@process-advisor/process-miner/shared/findingContext';

// Create context for menu state
const MenuContext = createContext(null);

const VariantGrid = ({variants, activities}) => {
  // Sample data based on your image

  let gridRef = React.useRef();
  const [openMenuRowId, setOpenMenuRowId] = useState(null);

  // Force AG-Grid to refresh cells when menu state changes
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.refreshCells({
        columns: ['actions'],
        force: true
      });
    }
  }, [openMenuRowId]);

  // Sleep utility function
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const onRowClickedFindingContext = useCallback((params) => {
    setFindingContextFromGridRow(params?.data || params?.node?.data || {}, 'Evidence - Variants', 'Variant');
  }, []);

  // Helper function to execute menu actions
  const executeMenuAction = async (action, data) => {
    setOpenMenuRowId(null); // Close menu
    await action(data);
  };

  // Get menu items structure (shared between context menu and actions column)
  const getMenuItems = (data) => {
    return {
      insights: [
        {
          name: 'Clusters',
          action: async () => {
            openProgressModal({status: 'Processing Clusters'});
            await sleep(200);
            await apiProvider.generateTasksFilteredArray({
              variantPaneVariants: false,
              type: 'variant',
              variant: data.variant_id,
              activity: 0,
              from: 0,
              to: 0
            });
            apiProvider.setDiagramCluster({source: 'variant'});
            closeProgressModal();
          }
        },
        {
          name: 'Influencers',
          action: async () => {
            openProgressModal({status: 'Processing Influencer Insights'});
            await sleep(200);
            await apiProvider.generateTasksFilteredArray({
              variantPaneVariants: false,
              type: 'variant',
              variant: data.variant_id,
              activity: 0,
              from: 0,
              to: 0
            });
            apiProvider.setDiagramInfluenceAnalysis({source: 'variant'});
            closeProgressModal();
          }
        },
        {
          name: 'Decompose',
          action: () => {
            apiProvider.generateTasksFilteredArray({
              variantPaneVariants: false,
              type: 'variant',
              variant: data.variant_id,
              activity: 0,
              from: 0,
              to: 0
            });
            apiProvider.setDiagramDecompositionTree({source: 'variant'});
          }
        },
        {
          name: 'Activity Analysis',
          action: () => {
            apiProvider.generateTasksFilteredArray({
              variantPaneVariants: false,
              type: 'variant',
              variant: data.variant_id,
              activity: 0,
              from: 0,
              to: 0
            });
            apiProvider.setTimeInActivitySource({source: 'variant'});
            apiProvider.setAction_Time({});
          }
        },
        {
          name: 'Histogram',
          action: () => {
            apiProvider.generateHistogramArray({
              variantPaneVariants: false,
              type: 'variant-tasks',
              variant: data.variant_id,
              activity: 0,
              from: 0,
              to: 0
            });
            apiProvider.histogram({});
          }
        }
      ],
      workItems: [
        {
          name: 'Detail',
          action: () => {
            apiProvider.setVariantDetailProps({variant: data.variant_id});
            apiProvider.taskDetail({});
          }
        },
        {
          name: 'History',
          action: () => {
            apiProvider.processAnalytics({
              variant: data.variant_id.toString(),
              object_type: 'Variant',
              object_name: data.variant,
              series_name: apiProvider.getEntityUpper() + 's'
            });
          }
        },
        {
          name: 'LifeCycle',
          action: () => {
            apiProvider.generateTasksFilteredArray({
              variantPaneVariants: false,
              type: 'variant',
              variant: data.variant_id,
              activity: 0,
              from: 0,
              to: 0
            });
            apiProvider.setTasksVisualizationSource({source: 'variant'});
            apiProvider.tasksVisualization();
            apiProvider.taskLifecycle({});
          }
        }
      ],
      events: [
        {
          name: 'Detail',
          action: () => {
            apiProvider.setVariantDetailProps({variant: data.variant_id});
            apiProvider.eventDetail({});
          }
        },
        {
          name: 'Histogram',
          action: () => {
            apiProvider.generateHistogramArray({
              variantPaneVariants: false,
              type: 'variant-events',
              variant: data.variant_id,
              activity: 0,
              from: 0,
              to: 0
            });
            apiProvider.histogram({});
          }
        }
      ],
      export: [
        {
          name: 'CSV Export (all)',
          action: () => gridRef.current.api.exportDataAsCsv({onlySelected: false})
        },
        {
          name: 'CSV Export (selected)',
          action: () => gridRef.current.api.exportDataAsCsv({onlySelected: true})
        },
        {
          name: 'XLS Export (all)',
          action: () => gridRef.current.api.exportDataAsExcel({onlySelected: false})
        },
        {
          name: 'XLS Export (selected)',
          action: () => gridRef.current.api.exportDataAsExcel({onlySelected: true})
        }
      ]
    };
  };

  // Actions menu renderer component
  // Actions menu renderer with Portal-based menu
  const ActionsMenuRenderer = (params) => {
    const { data } = params;
    
    if (data === undefined) {
      return '';
    }

    // Use React Context
    const menuContext = useContext(MenuContext);
    const openMenuRowId = menuContext?.openMenuRowId;
    const setOpenMenuRowId = menuContext?.setOpenMenuRowId;

    const [showSubMenu, setShowSubMenu] = useState(null);
    const [buttonPosition, setButtonPosition] = useState(null);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    const menuItems = getMenuItems(data);
    
    const isOpen = openMenuRowId === data.variant_id;

    // Update button position when menu opens
    useEffect(() => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setButtonPosition({
          top: Math.min(rect.bottom + 4, window.innerHeight - 260),
          left: Math.max(8, Math.min(rect.right - 200, window.innerWidth - 208)),
        });
      }
    }, [isOpen]);

    // Close menu when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (isOpen && menuRef.current && !menuRef.current.contains(event.target) &&
            buttonRef.current && !buttonRef.current.contains(event.target)) {
          setOpenMenuRowId(null);
          setShowSubMenu(null);
        }
      };
      
      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen, setOpenMenuRowId]);

    const handleButtonClick = () => {
      if (isOpen) {
        setOpenMenuRowId(null);
      } else {
        setOpenMenuRowId(data.variant_id);
      }
    };

    return (
      <>
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div ref={buttonRef}>
            <Button
              startIcon={<MoreIcon label="Actions" />}
              onClick={handleButtonClick}
            />
          </div>
        </div>

        {isOpen && buttonPosition && createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: `${buttonPosition.top}px`,
              left: `${buttonPosition.left}px`,
              backgroundColor: 'white',
              border: '1px solid #DFE1E6',
              borderRadius: '3px',
              boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
              zIndex: 10000,
              minWidth: '200px',
              marginTop: '4px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Insights submenu */}
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#172B4D',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F4F5F7';
                setShowSubMenu('insights');
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span>Insights</span>
              <span style={{ fontSize: '12px', color: '#5E6C84' }}>›</span>
              
              {showSubMenu === 'insights' && (
                <div
                  style={{
                    position: 'absolute',
                    right: '100%',
                    top: '0',
                    backgroundColor: 'white',
                    border: '1px solid #DFE1E6',
                    borderRadius: '3px',
                    boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
                    minWidth: '180px',
                    marginRight: '4px',
                    zIndex: 1001
                  }}
                  onMouseEnter={() => setShowSubMenu('insights')}
                  onMouseLeave={() => setShowSubMenu(null)}
                >
                  {menuItems.insights.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        setOpenMenuRowId(null);
                        executeMenuAction(item.action, data);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#172B4D'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F5F7'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {item.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Entity submenu */}
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#172B4D',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F4F5F7';
                setShowSubMenu('workItems');
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span>{apiProvider.getEntityUpper()}s</span>
              <span style={{ fontSize: '12px', color: '#5E6C84' }}>›</span>
              
              {showSubMenu === 'workItems' && (
                <div
                  style={{
                    position: 'absolute',
                    right: '100%',
                    top: '0',
                    backgroundColor: 'white',
                    border: '1px solid #DFE1E6',
                    borderRadius: '3px',
                    boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
                    minWidth: '180px',
                    marginRight: '4px',
                    zIndex: 1001
                  }}
                  onMouseEnter={() => setShowSubMenu('workItems')}
                  onMouseLeave={() => setShowSubMenu(null)}
                >
                  {menuItems.workItems.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        setOpenMenuRowId(null);
                        executeMenuAction(item.action, data);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#172B4D'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F5F7'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {item.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Events submenu */}
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#172B4D',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F4F5F7';
                setShowSubMenu('events');
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span>Events</span>
              <span style={{ fontSize: '12px', color: '#5E6C84' }}>›</span>
              
              {showSubMenu === 'events' && (
                <div
                  style={{
                    position: 'absolute',
                    right: '100%',
                    top: '0',
                    backgroundColor: 'white',
                    border: '1px solid #DFE1E6',
                    borderRadius: '3px',
                    boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
                    minWidth: '180px',
                    marginRight: '4px',
                    zIndex: 1001
                  }}
                  onMouseEnter={() => setShowSubMenu('events')}
                  onMouseLeave={() => setShowSubMenu(null)}
                >
                  {menuItems.events.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        setOpenMenuRowId(null);
                        executeMenuAction(item.action, data);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#172B4D'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F5F7'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {item.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Separator */}
            <div style={{ 
              height: '1px', 
              backgroundColor: '#DFE1E6', 
              margin: '4px 0' 
            }} />

            {/* Export submenu */}
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#172B4D',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F4F5F7';
                setShowSubMenu('export');
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span>Export</span>
              <span style={{ fontSize: '12px', color: '#5E6C84' }}>›</span>
              
              {showSubMenu === 'export' && (
                <div
                  style={{
                    position: 'absolute',
                    right: '100%',
                    top: '0',
                    backgroundColor: 'white',
                    border: '1px solid #DFE1E6',
                    borderRadius: '3px',
                    boxShadow: '0 4px 8px rgba(9, 30, 66, 0.25)',
                    minWidth: '200px',
                    marginRight: '4px',
                    zIndex: 1001
                  }}
                  onMouseEnter={() => setShowSubMenu('export')}
                  onMouseLeave={() => setShowSubMenu(null)}
                >
                  {menuItems.export.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        setOpenMenuRowId(null);
                        executeMenuAction(item.action, data);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#172B4D'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F4F5F7'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {item.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
      </>
    );
  };


    // Use AG Grid’s standard context menu; row-specific functions remain in Actions.





  const statusColors = [
  '#FFC400', // Yellow
  '#0052CC', // Blue
  '#00875A', // Dark green
  '#DE350B', // Dark red
  '#FF8B00', // Orange
  '#8777D9', // Purple
  '#00B8D9', // Cyan
  '#403294', // Dark purple
  '#97A0AF', // Light gray
  '#36B37E', // Green
  '#FFAB00', // Amber
  '#FF5630', // Red
  '#6554C0', // Indigo
  '#00A3BF', // Teal
  '#998DD9', // Light purple
  '#FF7452', // Coral
  '#2684FF', // Light blue
  '#57D9A3', // Mint green
  '#172B4D',  // Dark blue
  '#6B778C', // Gray
];

  // Status badge component for workflow steps
  const StatusBadge = ({ status }) => {
    const getStatusColor = (status) => {
      for (let i = 0; i < activities.length; i++) {
        if (activities[i].activity === status) return (statusColors[i])
      }
      return '#6B778C'
    };

    return (
      <span 
        style={{
          backgroundColor: getStatusColor(status),
          color: 'white',
          padding: '2px 5px',
          borderRadius: '2px',
          fontSize: '11px',
          fontWeight: '600',
          margin: '0 1px',
          lineHeight: '1.5'
        }}
      >
        {status}
      </span>
    );
  };

  // Custom cell renderer for workflow variant
  const WorkflowRenderer = ({ value }) => {
    const steps = value.split(' -> ');
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        flexWrap: 'wrap', 
        gap: '3px',
        padding: '4px 0',
        lineHeight: '1.4'
      }}>
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <StatusBadge status={step} />
            {index < steps.length - 1 && (
              <span style={{ 
                color: '#6B778C', 
                margin: '0 1px',
                fontSize: '10px'
              }}>
                →
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Percentage bar renderer
  const PercentageRenderer = ({ value }) => {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        width: '100%'
      }}>
        <div style={{
          backgroundColor: '#EBECF0',
          borderRadius: '3px',
          height: '8px',
          flex: 1,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div 
            style={{
              backgroundColor: '#0052CC',
              height: '100%',
              width: `${Math.min(value * 2, 100)}%`,
              borderRadius: '3px',
              transition: 'width 0.3s ease'
            }}
          />
        </div>
        <span style={{ 
          fontSize: '13px',
          fontWeight: '400',
          color: '#172B4D',
          minWidth: '40px'
        }}>
          {(value*100).toFixed(2)}%
        </span>
      </div>
    );
  };

const columnDefs = [
    {
      headerName: "Variant",
      field: "variant",
      flex: 3,
      cellRenderer: WorkflowRenderer,
      cellStyle: { 
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'flex-start'
      },
    },
    {
      headerName: apiProvider.getEntityUpper() + "s",
      field: "tasks",
      width: 130,
      cellStyle: { 
        textAlign: 'center',
        color: '#172B4D'
      }
    },
    {
      headerName: "% Total",
      field: "perc_total_tasks",
      width: 125,
      cellRenderer: PercentageRenderer,
      cellStyle: { 
        padding: '8px 12px',
        display: 'flex',
        textAlign: 'center',
        color: '#172B4D'
      }
    },
    {
      headerName: "Avg Duration",
      field: "avg_duration",
      width: 140,
      valueFormatter: ({ value }) => apiProvider.formatDuration({ seconds: value }),
      cellStyle: { 
        textAlign: 'center',
        color: '#172B4D'
      }
    },
    {
      headerName: "Total Duration",
      field: "total_duration",
      width: 140,
      valueFormatter: ({ value }) => apiProvider.formatDuration({ seconds: value }),
      cellStyle: { 
        textAlign: 'center',
        color: '#172B4D'
      }
    },    
    {
      headerName: "Std Deviation",
      field: "std_deviation",
      width: 140,
      valueFormatter: ({ value }) => apiProvider.formatDuration({ seconds: value }),
      cellStyle: { 
        textAlign: 'center',
        color: '#172B4D'
      }
    },
    {
      field: 'loop',
      headerName: 'Loops',
      width: 100,
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsMenuRenderer,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      suppressNavigable: true,
      suppressCellFocus: true,
      editable: false,
      width: 80,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0',
        overflow: 'visible'
      },
      cellClass: 'actions-cell',
      headerClass: 'actions-header'
    }
  ];
  const defaultColDef = {
    sortable: true,
    resizable: true,
    filter: true
  };

  const gridOptions = React.useMemo(() => ({
    headerHeight: 48,
    rowHeight: 40,
    animateRows: true,
    enableCellTextSelection: true,
    suppressRowClickSelection: true,
    getRowHeight: (params) => {
      // Get actual width of the variant column
      let variantColumnWidth = 700; // fallback default
      if (gridRef.current.api) {
        const variantColumn = gridRef.current.api.getColumn('variant')
        if (variantColumn) {
          //variantColumnWidth = variantColumn.getActualWidth();
        }
      }
      // Calculate estimated content width
      const steps = params.data.variant.split(' -> ');
      const estimatedWidth = steps.reduce((width, step) => {
        // More accurate estimation: ~7px per character + badge padding + margins
        return width + (step.length * 7) + 20; // 20px for padding, margins, arrows
      }, 0);
      
      // Add extra space for arrows between steps
      const arrowsWidth = (steps.length - 1) * 15; // ~15px per arrow with spacing
      const totalEstimatedWidth = estimatedWidth + arrowsWidth;
      
      // Account for cell padding (24px total: 12px left + 12px right)
      const availableWidth = variantColumnWidth - 24;
      // If content doesn't fit, allow for wrapping
      if (totalEstimatedWidth > availableWidth) {
        return 60; // taller for wrapped content
      }
      return 40; // normal height
    },

    /*
    : (params) => {
      // Wait for columns to be sized
      setTimeout(() => {
        const column = gridRef.current.api.getColumn('variant');
        const width = column.getActualWidth();
        console.log('Column width:', width);
        gridRef.current.api.resetRowHeights()
      }, 200);
    }
    */
  }), []);

  return (
    <MenuContext.Provider value={{ openMenuRowId, setOpenMenuRowId }}>
      <div style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backgroundColor: '#F4F5F7',
    }}>
      <div 
        style={{ 
          height: '600px',
          backgroundColor: 'white',
          borderRadius: '0 0 8px 8px',
          border: '1px solid #DFE1E6',
          borderTop: 'none'
        }}
        className="evidence-grid-shell evidence-ag-grid"
      >
        <style jsx>{`
          .ag-cell.actions-cell {
            overflow: visible !important;
            position: relative !important;
          }
          .ag-cell.actions-cell * {
            pointer-events: auto !important;
          }
          .ag-cell.actions-cell button {
            pointer-events: auto !important;
            cursor: pointer !important;
          }
          .ag-header-cell.actions-header .ag-header-cell-menu-button {
            display: none !important;
          }
        `}</style>
        
        <AgGridReact
          onRowClicked={onRowClickedFindingContext}
          ref={gridRef}
          theme={themeAlpine}
          rowData={variants}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
        />
      </div>
     
    </div>
    </MenuContext.Provider>
  );
};

export default VariantGrid;